import { useCallback, useEffect, useState } from "react";
import { Card, Funnel, ProvenanceTag } from "@/ui";
import { funnels } from "@/mock";
import { fmt } from "@/lib/format";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useFunnels } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { AigenTag, DemoNote, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";
import { FunnelBuilder } from "./FunnelBuilder";
import { loadFunnelSteps, saveFunnelSteps, serializeFunnelSteps, type FunnelStepDef } from "./funnelSteps";

export function Funnels() {
  const { siteId, range, live } = useWorkspace();

  // The property's funnel definition (persisted per property). Reload it when the
  // property changes; persist on every edit (with the correct id captured here so
  // switching properties can't cross-save one funnel onto another).
  const [steps, setSteps] = useState<FunnelStepDef[]>(() => loadFunnelSteps(siteId));
  useEffect(() => {
    setSteps(loadFunnelSteps(siteId));
  }, [siteId]);
  const updateSteps = useCallback(
    (next: FunnelStepDef[]) => {
      setSteps(next);
      saveFunnelSteps(siteId, next);
    },
    [siteId],
  );

  const funnelQ = useFunnels(siteId, range, serializeFunnelSteps(steps));

  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: funnelQ.isLoading,
    isError: funnelQ.isError,
    hasData: !!funnelQ.data,
  });

  const isLive = state === "live";

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Funnels"
        desc="Define your conversion steps and see where momentum holds — and where it leaks."
        action={
          isLive ? <AigenTag /> : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="posthog" />
              <SampleChip />
            </span>
          )
        }
      />

      {state === "loading" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-[260px]" />
          <Skeleton className="h-[260px]" />
        </div>
      ) : isLive ? (
        <div className="space-y-4">
          {/* User-defined, ordered steps → drives the analytics-svc ?steps= DSL. */}
          <FunnelBuilder steps={steps} onChange={updateSteps} />

          {(() => {
            const data = funnelQ.data;
            const rows = data?.steps ?? [];
            const entered = data?.entered ?? 0;
            return (
              <Card className="p-5">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
                      Conversion funnel
                    </h3>
                    <p className="mt-0.5 text-[12px] text-subtle">
                      {fmt(data?.entered ?? 0)} entered · {fmt(data?.completed ?? 0)} completed
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-right">
                    <div className="font-mono text-[12px] text-muted">
                      {(data?.overall_pct ?? 0).toFixed(1)}%
                    </div>
                    <span className="flex items-center gap-2">
                      <AigenTag />
                      {data?.seeded && <DemoNote />}
                    </span>
                  </div>
                </div>
                {rows.length > 0 && entered > 0 ? (
                  <Funnel steps={rows.map((s) => ({ label: s.label, value: s.visitors }))} />
                ) : (
                  <EmptyState
                    title="No one has entered this funnel yet"
                    detail="These steps are live — as visitors move through them, the funnel fills in. Adjust the steps above to measure a different path."
                  />
                )}
              </Card>
            );
          })()}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {funnels.map((f) => {
            const first = f.steps[0]!.value;
            const last = f.steps[f.steps.length - 1]!.value;
            const overall = ((last / first) * 100).toFixed(1);
            return (
              <Card key={f.name} className="p-5">
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
                      {f.name}
                    </h3>
                    <p className="mt-0.5 text-[12px] text-subtle">
                      {fmt(first)} entered · {fmt(last)} completed
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-right">
                    <div className="font-mono text-[12px] text-muted">{overall}%</div>
                    <span className="flex items-center gap-2">
                      <ProvenanceTag source={f.source} />
                      <SampleChip />
                    </span>
                  </div>
                </div>
                <Funnel steps={f.steps} />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
